// План по месяцам — экран из макета design/redesign.mockup.html. Решение
// владельца от 28 октября 2026 (docs/02-decisions.md): план есть у каждого
// ученика, порядок — учебник 9 класса там, где карта не требует базу раньше,
// темп по умолчанию — «вся карта к экзамену».
//
// Расчёт на сервере (`schedule.view`, convex/lib/schedule.ts), мутацией на
// открытии: плану нужно состояние всех навыков, подписка на него запрещена
// инвариантом 4. Здесь только раскладка по месяцам и действия.
//
// «Взять сейчас» — существующая цель (`goal.setAim`): её маршрут идёт первым и в
// плане, и в предложениях нового навыка. Своего хранилища у плана нет.
//
// Отставания как упрёка нет: при пропуске план растягивается, меняется только
// «нужно в неделю», а черта «при нынешнем темпе — досюда» показывает цену.

import type { FunctionReturnType } from "convex/server";
import { useCallback, useEffect, useState } from "react";
import { api, useMutation } from "../api";
import { plural } from "../lib/dates";
import { CoachNote } from "../ui/CoachNote";

type Pace = "exam" | "promise";
type Schedule = FunctionReturnType<typeof api.schedule.view>;
type Block = Schedule["blocks"][number];

const MONTHS = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const MONTH_NAME = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
const KIND: Record<Block["kind"], string> = { alg: "Алгебра", geo: "Геометрия", word: "Задачи", adv: "Повышенный" };

function parts(date: string): [number, number, number] {
  const [y, m, d] = date.split("-").map(Number);
  return [y!, m!, d!];
}

/** «26 окт – 1 ноя» или «2–8 ноя». */
function range(from: string, to: string): string {
  const [, fm, fd] = parts(from);
  const [, tm, td] = parts(to);
  return fm === tm ? `${fd}–${td} ${MONTHS[tm - 1]}` : `${fd} ${MONTHS[fm - 1]} – ${td} ${MONTHS[tm - 1]}`;
}

const one = (n: number) => n.toLocaleString("ru-RU", { maximumFractionDigits: 1 });

export function Plan({
  learnerKey,
  onOpenAtlas,
}: {
  learnerKey: string;
  /** «Открыть» у темы этой недели — карта навыков на её первом навыке. */
  onOpenAtlas: (skillId: string) => void;
}) {
  const view = useMutation(api.schedule.view);
  const setAim = useMutation(api.goal.setAim);
  const clearAim = useMutation(api.goal.clearAim);
  const [pace, setPace] = useState<Pace>("exam");
  const [plan, setPlan] = useState<Schedule | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(
    (p: Pace) =>
      view({ learnerKey, pace: p })
        .then((next) => {
          setPlan(next);
          return next;
        })
        .catch((e: unknown) => {
          console.error(e);
          return null;
        }),
    [learnerKey, view],
  );
  useEffect(() => {
    void load(pace);
  }, [load, pace]);

  if (plan === null) {
    return (
      <div className="stack">
        <div className="skeleton" style={{ width: 180 }} />
        <div className="skeleton" style={{ width: 320, height: 34 }} />
        <div className="card">
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  const take = (b: Block) => {
    // Цель — последний навык блока: его маршрут тянет за собой остальные навыки
    // темы, которые ему нужны, и они встают на эту неделю.
    const last = b.skills[b.skills.length - 1]!;
    setBusy(b.skills[0]!.skillId);
    void setAim({ learnerKey, skillId: last.skillId })
      .then(() => load(pace))
      .then((next) => {
        if (next === null) return;
        // Честно о том, что встало перед темой: у того, кто не прошёл базу,
        // к ней ведут предпосылки, и «тема — следующая» было бы неправдой.
        const at = next.blocks.findIndex((x) => x.topicId === b.topicId);
        const before = next.blocks.slice(0, Math.max(0, at)).reduce((n, x) => n + x.skills.length, 0);
        const start = at >= 0 ? next.blocks[at]! : null;
        setNotice(
          before === 0
            ? `«${b.title}» — первая в плане. Новые навыки система будет предлагать отсюда.`
            : `«${b.title}» — так рано, как позволяет карта: перед ней ${before} ${plural(before, "навык", "навыка", "навыков")}, без которых её не решить${start ? `, начало — ${range(start.from, start.to)}` : ""}.`,
        );
      })
      .finally(() => setBusy(null));
  };

  // Группы: эта неделя, дальше по месяцам начала, в конце — что не помещается.
  type Group = { key: string; label: string; blocks: Block[] };
  const groups: Group[] = [];
  for (const b of plan.blocks) {
    // Сверх цели — отдельной группой в самом конце, после черты экзамена.
    const key = b.extra ? "extra" : b.late ? "late" : b.fromWeek === 0 ? "now" : b.from.slice(0, 7);
    let g = groups[groups.length - 1];
    if (g === undefined || g.key !== key) {
      const [, m] = parts(b.from);
      g = {
        key,
        label: key === "now" ? "Эта неделя" : key === "late" ? "Не помещается" : key === "extra" ? "Сверх цели" : MONTH_NAME[m - 1]!,
        blocks: [],
      };
      groups.push(g);
    }
    g.blocks.push(b);
  }

  const afterExam = groups.find((g) => g.key === "late" || g.key === "extra");

  // Черта «при нынешнем темпе — досюда»: после блока, на котором накопленное
  // число навыков переходит то, что успевается к экзамену нынешним темпом.
  let cum = 0;
  let markerAfter: Block | null = null;
  if (plan.nowReach < plan.remaining) {
    for (const b of plan.blocks) {
      cum += b.skills.length;
      if (cum > plan.nowReach) break;
      markerAfter = b;
    }
  }
  const markerFirst = plan.nowReach < plan.remaining && markerAfter === null;
  const examLabel = (() => {
    const [, m, d] = parts(plan.examDate);
    return `${d} ${MONTHS[m - 1]}`;
  })();
  const marker = (
    <div className="plan-marker" role="note">
      при нынешнем темпе (≈ {one(plan.actualPerWeek)} в неделю) к {examLabel} — досюда
    </div>
  );

  return (
    <div className="plan">
      <div>
        <div className="eyebrow">План до экзамена</div>
        <h1 className="h1">План по месяцам</h1>
        <p className="lede">
          Пересчитывается при каждом открытии по тому, что пройдено. Порядок — школьный
          учебник 9 класса, кроме мест, где сначала нужна база.
        </p>
      </div>

      <section className="card plan-stats" aria-label="Темп">
        <div>
          <span className="eyebrow">Осталось</span>
          <b className="num">{plan.remaining}</b>
          <span>
            {plural(plan.remaining, "навык", "навыка", "навыков")} · {plan.weeksLeft}{" "}
            {plural(plan.weeksLeft, "неделя", "недели", "недель")}
          </span>
        </div>
        <div>
          <span className="eyebrow">Нужно в неделю</span>
          <b className="num">{plan.needPerWeek}</b>
          <span>чтобы к экзамену пройти всё, что нужно цели</span>
        </div>
        <div>
          <span className="eyebrow">Открываешь в неделю</span>
          <b className="num">≈ {one(plan.actualPerWeek)}</b>
          <span>за последние две недели</span>
        </div>
      </section>

      {/* Темп ниже нужного — тренер говорит цену и один шаг, без упрёка. */}
      {plan.remaining > 0 && plan.actualPerWeek < plan.needPerWeek ? (
        <CoachNote
          situation="behind"
          vars={{ need: plan.needPerWeek, weeks: plan.weeksLeft }}
          seedParts={[learnerKey, plan.date]}
        />
      ) : null}

      <div className="plan-bar">
        <div className="plan-seg" role="group" aria-label="Темп плана">
          <button type="button" aria-pressed={pace === "exam"} onClick={() => setPace("exam")}>
            Всё к экзамену · {plan.needPerWeek}/нед
          </button>
          <button type="button" aria-pressed={pace === "promise"} onClick={() => setPace("promise")}>
            Моё обязательство · {plan.daysPerWeek}/нед
          </button>
        </div>
        {plan.aim === null ? null : (
          <p className="plan-aim">
            Первой идёт {plan.aim.topic === null ? <>цель «{plan.aim.name}»</> : <>тема «{plan.aim.topic}»</>}.{" "}
            <button
              type="button"
              className="link"
              onClick={() => {
                setNotice(null);
                void clearAim({ learnerKey }).then(() => load(pace));
              }}
            >
              Вернуть порядок учебника
            </button>
          </p>
        )}
      </div>

      {notice === null ? null : (
        <div className="notice notice--info" role="status">
          {notice}
        </div>
      )}

      {plan.remaining === 0 ? (
        <section className="card">
          <h3>Карта пройдена целиком</h3>
          <p className="muted">Планировать нечего: дальше — повторения и задачи формата экзамена.</p>
        </section>
      ) : null}

      {markerFirst ? marker : null}
      {groups.map((g) => (
        <section key={g.key} className={`plan-month${g.key === "now" ? " plan-month--now" : ""}`}>
          {/* Черта экзамена — перед первой группой, которая за ним: «не помещается» или «сверх цели». */}
          {g === afterExam ? <div className="plan-marker plan-marker--exam">{examLabel} — {plan.examTitle}</div> : null}
          {g.key === "now" ? (
            <span className="stamp" aria-hidden="true">
              В работе
            </span>
          ) : null}
          <div className="plan-month__head">
            <h2>{g.label}</h2>
            <span className="muted">
              {g.blocks.length} {plural(g.blocks.length, "тема", "темы", "тем")} ·{" "}
              {g.blocks.reduce((n, b) => n + b.skills.length, 0)}{" "}
              {plural(
                g.blocks.reduce((n, b) => n + b.skills.length, 0),
                "навык",
                "навыка",
                "навыков",
              )}
            </span>
          </div>
          <ol className="plan-rows">
            {g.blocks.map((b) => {
              const now = !b.late && b.fromWeek === 0;
              return (
                <li key={b.skills[0]!.skillId}>
                  <div className={`plan-row${now ? " plan-row--now" : ""}${b.late ? " plan-row--late" : ""}`}>
                    <span className="plan-row__when">{b.late ? "после экзамена" : range(b.from, b.to)}</span>
                    <span className="plan-row__what">
                      <span className={`plan-tag plan-tag--${b.kind}`}>
                        {KIND[b.kind]}
                        {b.ref && b.ref !== KIND[b.kind].toLowerCase() ? ` · ${b.ref}` : ""}
                      </span>
                      <b>{b.title}</b>
                      <small className="muted">{b.skills.map((s) => s.name).join(" · ")}</small>
                    </span>
                    <span className="plan-row__state">
                      {now
                        ? `идёт · ${b.topicDone}/${b.topicTotal}`
                        : `${b.skills.length} ${plural(b.skills.length, "навык", "навыка", "навыков")}`}
                    </span>
                    <span className="plan-row__act">
                      {now ? (
                        <button type="button" className="btn" onClick={() => onOpenAtlas(b.skills[0]!.skillId)}>
                          Открыть
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn"
                          disabled={busy !== null}
                          onClick={() => take(b)}
                        >
                          Взять сейчас
                        </button>
                      )}
                    </span>
                  </div>
                  {markerAfter === b ? marker : null}
                </li>
              );
            })}
          </ol>
        </section>
      ))}
      {afterExam !== undefined || plan.remaining === 0 ? null : (
        <div className="plan-marker plan-marker--exam">{examLabel} — {plan.examTitle}</div>
      )}

      <div className="plan-why">
        <section className="card">
          <h3>Почему такой порядок</h3>
          <p className="muted">
            Темы идут как в школе, чтобы тема недели совпадала с уроками. Если для темы не
            хватает базы 5–8 класса, база встаёт прямо перед ней.
          </p>
        </section>
        <section className="card">
          <h3>Взять тему раньше</h3>
          <p className="muted">
            «Взять сейчас» ставит тему первой, остальное сдвигается. Новые навыки система тоже
            начнёт предлагать с неё.
          </p>
        </section>
        <section className="card">
          <h3>Если пропустил</h3>
          <p className="muted">
            Ничего не сгорает: план растягивается на оставшиеся недели, меняется только число
            «нужно в неделю».
          </p>
        </section>
      </div>
    </div>
  );
}
